<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-sm space-y-10">
      <div>
        <img class="mx-auto h-30 w-auto dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <img class="mx-auto h-30 w-auto not-dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          Sign in to your account
        </h2>
        <p
          v-if="invitationPending"
          class="mt-4 rounded-md bg-indigo-50 p-3 text-center text-sm text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
          data-testid="invitation-hint"
        >
          Sign in to accept your organization invitation. New here? Create an account with the invited
          email address.
        </p>
      </div>
      <form class="space-y-6" @submit.prevent="onSubmit">
        <div v-if="!auth.mfaRequired">
          <div class="col-span-2">
            <input
              id="username"
              name="username"
              type="text"
              autocomplete="username"
              required
              autofocus
              aria-label="Username"
              class="block w-full rounded-t-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Username"
              v-model="username"
              :disabled="auth.isAuthenticated"
            />
          </div>
          <div class="-mt-px">
            <input
              id="password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
              aria-label="Password"
              class="block w-full rounded-b-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Password"
              v-model="password"
              :disabled="auth.isAuthenticated"
            />
          </div>
          <div class="mt-2 flex justify-end">
            <router-link
              :to="{ name: 'ForgotPassword' }"
              class="text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Forgot password?
            </router-link>
          </div>
        </div>

        <div v-else>
          <label for="otp" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Enter 2FA code</label
          >
          <input
            id="otp"
            ref="otpInput"
            name="otp"
            type="text"
            inputmode="numeric"
            pattern="\d{6}"
            maxlength="6"
            autocomplete="one-time-code"
            required
            aria-label="One-time code"
            class="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
            placeholder="6-digit code"
            v-model="otp"
            :disabled="auth.loading"
          />
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Open your authenticator app and enter the current code.
          </p>
        </div>

        <div class="space-y-2">
          <button
            type="submit"
            :disabled="auth.loading || auth.isAuthenticated || auth.isLoginLocked"
            class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
          >
            <span v-if="auth.loading">{{ auth.mfaRequired ? 'Verifying…' : 'Signing in…' }}</span>
            <span v-else>{{ auth.mfaRequired ? 'Verify code' : 'Sign in' }}</span>
          </button>
          <button
            v-if="auth.mfaRequired"
            type="button"
            class="w-full text-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            @click="auth.cancelMfa()"
          >
            Use a different account
          </button>
          <p v-if="auth.error" class="text-sm text-red-600 dark:text-red-400" role="alert">
            {{ auth.error }}
          </p>
          <p v-if="auth.isAuthenticated" class="text-sm text-green-700 dark:text-green-400">
            Signed in. Your token is stored for this session.
          </p>
        </div>
      </form>

      <p
        v-if="!auth.isAuthenticated"
        class="text-center text-sm/6 text-gray-500 dark:text-gray-400"
      >
        No account yet?
        {{ ' ' }}
        <router-link
          :to="{ name: 'Register', query: returnQuery }"
          class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >Sign up!</router-link
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import {useAuthStore} from '@/stores/auth';
import {useRoute, useRouter} from 'vue-router';
import {isInvitationRedirect, redirectTarget} from '@/lib/redirect';

onMounted(() => {
  let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  // Setting explicit canonical links as requested for SEO duplicate content prevention
  link.setAttribute('href', `${window.location.origin}/login`);
});

onUnmounted(() => {
  const link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.remove();
  }
});

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();
const returnTo = redirectTarget(route.query);
const returnQuery = returnTo ? { redirect: returnTo } : {};
const invitationPending = isInvitationRedirect(route.query);
const username = ref('');
const password = ref('');
const otp = ref('');
const otpInput = ref<HTMLInputElement | null>(null);

watch(
  () => auth.mfaRequired,
  async (required) => {
    if (required) {
      await nextTick();
      otpInput.value?.focus();
    }
  },
);

async function onSubmit() {
  try {
    if (!auth.mfaRequired) {
      if (!username.value || !password.value) return;
      await auth.login({ username: username.value, password: password.value });
      // After step 1, if MFA is required, the UI will switch to OTP input; otherwise, proceed.
    } else {
      if (!otp.value) return;
      await auth.verifyMfa(otp.value);
    }

    if (auth.isAuthenticated) {
      // Clear inputs after a fully successful login (including MFA when applicable)
      username.value = '';
      password.value = '';
      otp.value = '';
      await router.push(returnTo ?? { name: 'Dashboard' });
    }
  } catch {
    // Error already set in store; nothing else required here.
  }
}
</script>
